import { app } from "modular-openscriptjs";
import { authenticated, fireEvent, afterAuth, inform, redirect, empty } from "../helpers";
import { STRINGS, api, gc } from "../declarations";
import { Preferences } from "@capacitor/preferences";

export class Middleware {
	/**
	 * Ensures the user is authenticated
	 *
	 * Saves `location.pathname + location.search` (not just the pathname)
	 * as "redirect-url-after-auth" so a query string on the original page
	 * (e.g. "?it=<token>" on an invitation-accept link) survives the
	 * login / phone-update / OTP-verification detour. Without the query
	 * string, the post-auth redirect lands on a bare "/invitations/accept"
	 * with no token, which then falls through to "home" -- and "home"
	 * requires an already-selected company, so a brand-new invitee with no
	 * company yet gets funneled into "Register New" instead of back to the
	 * invite they were trying to accept.
	 *
	 * @param {function} next
	 * @returns {Middleware|boolean|null|undefined}
	 */
	auth(next) {
		if (authenticated()) {
			const route = app("router");
			const user = gc.user.value;

			if (!user?.phone) {
				if (!route.is("user.edit-profile")) {
					let url = location.pathname + location.search;
					Preferences.set({ key: "redirect-url-after-auth", value: url });
					inform("Please update your phone number to continue", STRINGS.WARNING);
					route.to("user.edit-profile");
					return null;
				}
			} else {
				if (!route.is("user.verify-identity") && !route.is("user.edit-profile")) {
					const verifiedAt = window.otpVerifiedAt || 0;
					const now = Date.now();
					const isExpired = !verifiedAt || (now - verifiedAt > 24 * 60 * 60 * 1000);

					if (isExpired) {
						let url = location.pathname + location.search;
						Preferences.set({ key: "redirect-url-after-auth", value: url });

						api.post("/auth/send/otp").catch(console.error);

						inform("Please verify your identity with OTP", STRINGS.INFO);
						route.to("user.verify-identity");
						return null;
					}
				}
			}

			if(typeof next === "undefined") {
				return this;
			}
			else if(typeof next === "function") {
				return next();
			}
			else {
				return true;
			}
		}

		let url = location.pathname + location.search;
		Preferences.set({ key: "redirect-url-after-auth", value: url });

		inform("Please log in to continue", STRINGS.ERROR);
		app("router").to("user.login");
		return null;
	}

	/**
	 * Ensures the company is selected
	 * @param {function} next
	 * @returns {any}
	 */
	company(next) {
		if (window.currentCompany?.id) {
			if(typeof next === "undefined") {
				return this;
			}
			else if(typeof next === "function") {
				return next();
			}
			else {
				return true;
			}
		}
		inform("Please select a company to continue", STRINGS.INFO);
		app("router").to("business.select");
		return null;
	}

	branches(next) {
		if (!empty(window.currentCompany?.branches)) {
			if(typeof next === "undefined") {
				return this;
			}
			else if(typeof next === "function") {
				return next();
			}
			else {
				return true;
			}
		}
		inform("Add at least one branch to continue", STRINGS.INFO);

		app("router").to("business.manage.branches", {companyId: window.currentCompany?.id});
		return null;
	}

	/**
	 * Ensures the company has set its weekly opening hours (see
	 * App\Models\Availability on the backend) before letting them into the
	 * rest of the business dashboard - same onboarding-gate pattern as
	 * branches() above. window.currentCompany.availability is populated by
	 * CompanyController::index()/store() (eager-loaded there specifically
	 * so this check doesn't need its own request), and refreshed in place
	 * by ManageAvailability.js right after a successful save, so this
	 * clears on the very next navigation without needing a re-login.
	 *
	 * Deliberately NOT chained onto "business.manage.availability" or
	 * "business.manage.branches" themselves (same reason branches() isn't
	 * chained onto "business.manage.branches") - otherwise a business with
	 * nothing set up yet could never reach the page that lets them set it.
	 */
	availability(next) {
		if (!empty(window.currentCompany?.availability?.time_slots)) {
			if(typeof next === "undefined") {
				return this;
			}
			else if(typeof next === "function") {
				return next();
			}
			else {
				return true;
			}
		}
		inform("Set your weekly opening hours to continue", STRINGS.INFO);

		app("router").to("business.manage.availability", {companyId: window.currentCompany?.id});
		return null;
	}
}
